/**
 * DataSourcesSection - Manages data source references for a dataset
 * 
 * CHANGE HISTORY:
 * - 251201_0245 danielbeer: Created adaptive data sources section
 * - 251201_0445 danielbeer: Refactored to use Interactr integration
 * 
 * ARCHITECTURE:
 * - Displays data source references (not full configs)
 * - Launches Interactr for external sources
 * - Launches Profylr for internal collections
 * - Launches Fylr for file operations
 */

import React, { useState } from 'react';
import { DataSourceManagerUI } from '@opcentral/interactr';
import type { DataSourceConfig } from '@opcentral/interactr';
import type { Dataset, DataSourceReference, InteractrSourceReference } from '../types/DatasetTypes';
import { isInteractrSource, isProfylrSource, isFylrSource } from '../types/DataSourceReference';
import { ImprovedModal } from './ImprovedModal';
import './DataSourcesSection.css';

export interface DataSourcesSectionProps {
  dataset: Dataset;
  onAddSource: (sourceRef: DataSourceReference) => void;
  onRemoveSource: (sourceRef: DataSourceReference) => void;
  onUpdateSource?: (sourceRef: DataSourceReference, updates: Partial<DataSourceReference>) => void;
}

export function DataSourcesSection({
  dataset,
  onAddSource,
  onRemoveSource,
  onUpdateSource
}: DataSourcesSectionProps) {
  const [showInteractrModal, setShowInteractrModal] = useState(false);
  const [showProfylrPicker, setShowProfylrPicker] = useState(false);
  const [showFylrPicker, setShowFylrPicker] = useState(false);

  const handleInteractrSourceSave = (source: DataSourceConfig) => {
    // Convert Interactr source to reference
    const sourceRef: InteractrSourceReference = {
      interactrSourceId: source.name, // TODO: Use actual ID when Interactr provides it
      name: source.name,
      type: source.type,
      enabled: true,
      cachedInfo: {
        server: source.config.server,
        database: source.config.database
      }
    };
    
    onAddSource(sourceRef);
    setShowInteractrModal(false);
  };

  return (
    <div className="data-sources-section">
      {/* Header */}
      <div className="dss-header">
        <h3>Data Sources</h3>
        <div className="dss-add-buttons">
          <button 
            onClick={() => setShowInteractrModal(true)}
            className="btn btn-primary"
          >
            🔌 Add Interactr Source
          </button>
          <button 
            onClick={() => setShowProfylrPicker(true)}
            className="btn btn-secondary"
            disabled
          >
            📊 Add Profylr Collection
          </button>
          <button 
            onClick={() => setShowFylrPicker(true)}
            className="btn btn-secondary"
            disabled
          >
            📁 Add Fylr Files
          </button>
        </div>
      </div>

      {/* Source List */}
      <div className="dss-sources">
        {dataset.sources.length === 0 ? (
          <div className="dss-empty">
            <div className="dss-empty-icon">🔌</div>
            <div className="dss-empty-title">No Data Sources</div>
            <div className="dss-empty-description">
              Add a data source to start aggregating data
            </div>
          </div>
        ) : (
          dataset.sources.map((sourceRef, index) => (
            <SourceReferenceCard
              key={index}
              sourceRef={sourceRef}
              onRemove={() => onRemoveSource(sourceRef)}
              onToggle={() => onUpdateSource?.(sourceRef, { enabled: !sourceRef.enabled })}
            />
          ))
        )}
      </div>

      {/* Interactr Modal */}
      <ImprovedModal
        isOpen={showInteractrModal}
        onClose={() => setShowInteractrModal(false)}
        title="Add Data Source"
        initialWidth={900}
        initialHeight={700}
      >
        <DataSourceManagerUI
          mode="create"
          onSave={handleInteractrSourceSave}
          onCancel={() => setShowInteractrModal(false)}
        />
      </ImprovedModal>

      {/* Profylr Picker (Coming Soon) */}
      <ImprovedModal
        isOpen={showProfylrPicker}
        onClose={() => setShowProfylrPicker(false)}
        title="Profylr Collection Picker"
        initialWidth={600}
        initialHeight={400}
      >
        <div className="coming-soon">
          <p>Coming Soon</p>
          <button onClick={() => setShowProfylrPicker(false)} className="btn btn-primary">
            Close
          </button>
        </div>
      </ImprovedModal>

      {/* Fylr Picker (Coming Soon) */}
      <ImprovedModal
        isOpen={showFylrPicker}
        onClose={() => setShowFylrPicker(false)}
        title="Fylr File Picker"
        initialWidth={600}
        initialHeight={400}
      >
        <div className="coming-soon">
          <p>Coming Soon</p>
          <button onClick={() => setShowFylrPicker(false)} className="btn btn-primary">
            Close
          </button>
        </div>
      </ImprovedModal>
    </div>
  );
}

interface SourceReferenceCardProps {
  sourceRef: DataSourceReference;
  onRemove: () => void;
  onToggle: () => void;
}

function SourceReferenceCard({ sourceRef, onRemove, onToggle }: SourceReferenceCardProps) {
  if (isInteractrSource(sourceRef)) {
    return (
      <div className={`source-card interactr ${!sourceRef.enabled ? 'disabled' : ''}`}>
        <div className="source-card-header">
          <div className="source-card-icon">🔌</div>
          <div className="source-card-info">
            <div className="source-card-name">{sourceRef.name}</div>
            <div className="source-card-type">{sourceRef.type}</div>
          </div>
          <div className="source-card-actions">
            <button 
              onClick={onToggle}
              className="btn-icon"
              title={sourceRef.enabled ? 'Disable' : 'Enable'}
            >
              {sourceRef.enabled ? '✓' : '○'}
            </button>
            <button 
              onClick={onRemove}
              className="btn-icon btn-danger"
              title="Remove"
            >
              ×
            </button>
          </div>
        </div>
        {sourceRef.cachedInfo && (
          <div className="source-card-details">
            {sourceRef.cachedInfo.server && (
              <span className="source-detail">
                <strong>Server:</strong> {sourceRef.cachedInfo.server}
              </span>
            )}
            {sourceRef.cachedInfo.database && (
              <span className="source-detail">
                <strong>Database:</strong> {sourceRef.cachedInfo.database}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (isProfylrSource(sourceRef)) {
    return (
      <div className={`source-card profylr ${!sourceRef.enabled ? 'disabled' : ''}`}>
        <div className="source-card-header">
          <div className="source-card-icon">📊</div>
          <div className="source-card-info">
            <div className="source-card-name">{sourceRef.name}</div>
            <div className="source-card-type">Profylr Collection</div>
          </div>
          <div className="source-card-actions">
            <button onClick={onToggle} className="btn-icon">
              {sourceRef.enabled ? '✓' : '○'}
            </button>
            <button onClick={onRemove} className="btn-icon btn-danger">×</button>
          </div>
        </div>
        <div className="source-card-details">
          <span className="source-detail">
            <strong>Collection:</strong> {sourceRef.profylrCollection}
          </span>
        </div>
      </div>
    );
  }

  if (isFylrSource(sourceRef)) {
    return (
      <div className={`source-card fylr ${!sourceRef.enabled ? 'disabled' : ''}`}>
        <div className="source-card-header">
          <div className="source-card-icon">📁</div>
          <div className="source-card-info">
            <div className="source-card-name">{sourceRef.name}</div>
            <div className="source-card-type">Fylr Files</div>
          </div>
          <div className="source-card-actions">
            <button onClick={onToggle} className="btn-icon">
              {sourceRef.enabled ? '✓' : '○'}
            </button>
            <button onClick={onRemove} className="btn-icon btn-danger">×</button>
          </div>
        </div>
        <div className="source-card-details">
          <span className="source-detail">
            <strong>Path:</strong> {sourceRef.fylrPath}
          </span>
        </div>
      </div>
    );
  }

  return null;
}
